import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';

import { openModal } from '../state/actions/ui/modal';
import { modalIds } from '../components/modals/SuperModal/types';
import { useSurveillanceRoutes } from './useSurveillanceRoutes';

/**
 * Aviso a sala del tramo (2026-09-24) — UNA sola lógica para sus dos puertas:
 * la card del tramo en «Órdenes asociadas» y la columna «Vincular» de la tabla
 * de Tramos de «Vista previa» (2026-10-08).
 *
 * El aviso vive en el tramo, así que se corrige ahí: vincularlo a una OT,
 * cambiarlo a otra (un aviso, una OT) o devolverlo a pendiente. Mismo modal de
 * OTs que «Vincular OT» del recorrido, con su propio título; ligar el aviso
 * liga también el recorrido (un gesto = un hecho). Al quitarlo, el back mira
 * si a la OT le queda algún motivo para seguir en el recorrido (§ 15) — por
 * eso `onChanged` tiene que revalidar también el recorrido.
 *
 * `onChanged` corre tras cada gesto que llega al servidor (o se encola).
 */
export const useSegmentAvisoLink = (onChanged?: () => void) => {
  const dispatch = useDispatch();
  const { linkExternalOrderToSegment, unlinkOrderFromSegment } =
    useSurveillanceRoutes();

  const pickOrderForSegment = useCallback(
    (
      onPicked: (orderId: string) => Promise<unknown>,
      avisoAt?: string | null
    ) => {
      dispatch(
        openModal(modalIds.RELATE_ORDER_TO_ROUTE, {
          target: 'aviso',
          avisoAt: avisoAt ?? null,
          onConfirm: async (orderId: string) => {
            await onPicked(orderId);
            onChanged?.();
          },
        })
      );
    },
    [dispatch, onChanged]
  );

  /** Vincula el aviso del tramo a la OT que se elija en el modal. */
  const linkAviso = useCallback(
    (segmentId: string, avisoAt?: string | null) => {
      pickOrderForSegment(
        (orderId) => linkExternalOrderToSegment(segmentId, orderId),
        avisoAt
      );
    },
    [pickOrderForSegment, linkExternalOrderToSegment]
  );

  /** Cambia la OT del aviso: desvincula la actual y vincula la elegida. */
  const changeAviso = useCallback(
    (segmentId: string, currentOrderId: string, avisoAt?: string | null) => {
      pickOrderForSegment(async (orderId) => {
        if (orderId === currentOrderId) return;
        // Un gesto, un aviso: el desvínculo va en silencio y avisa el vínculo.
        await unlinkOrderFromSegment(segmentId, currentOrderId, {
          silent: true,
        });
        await linkExternalOrderToSegment(segmentId, orderId);
      }, avisoAt);
    },
    [pickOrderForSegment, unlinkOrderFromSegment, linkExternalOrderToSegment]
  );

  // Tramo cuyo desvínculo está viajando (revisión M2): la tabla lo pinta sin
  // acciones hasta que vuelve, y el ref descarta un segundo toque.
  const [unlinkingSegmentId, setUnlinkingSegmentId] = useState<string | null>(
    null
  );
  const unlinkingRef = useRef<string | null>(null);

  /**
   * Devuelve el aviso a pendiente. La confirmación es la de la card: el menú
   * de dos toques (`TagActionsMenu`). No relanza: si falla, el hook de la
   * mutation ya avisa con un toast, y una promesa rechazada sin capturar
   * acabaría en la consola y en Sentry como error suelto.
   */
  const unlinkAviso = useCallback(
    async (segmentId: string, currentOrderId: string) => {
      if (unlinkingRef.current) return;
      unlinkingRef.current = segmentId;
      setUnlinkingSegmentId(segmentId);
      try {
        await unlinkOrderFromSegment(segmentId, currentOrderId);
        onChanged?.();
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            feature: 'surveillance-route',
            component: 'useSegmentAvisoLink',
          },
        });
      } finally {
        unlinkingRef.current = null;
        setUnlinkingSegmentId(null);
      }
    },
    [unlinkOrderFromSegment, onChanged]
  );

  return { linkAviso, changeAviso, unlinkAviso, unlinkingSegmentId };
};
